import React from 'react';

import Link from './Link';
import {useAppContext} from '../hooks';
import {cx} from '../util';
import AcedBadge from '../../assets/images/aced_badge.svg';

/**
 * Whether to show the rebrand announcement to a returning user. New users get
 * the welcome voicing of the same modal via shouldShowOnboardingVideo.
 */
export const shouldShowRebrandAnnouncement = (currentUser) => {
  if (!currentUser) return false;
  return !currentUser.data?.has_seen_rebrand_announcement;
};

/**
 * Master toggle for the "previously Exponent" badge that call sites can
 * request next to the Aced wordmark (see BrandLockup's fkaBadge prop).
 * Flip to false to drop the badge everywhere at once.
 */
export const FKA_BADGE_ENABLED = true;

/**
 * Whether page titles end in "Aced (formerly Exponent)" rather than "Aced".
 * The suffix costs about twenty characters of every title; flip to false
 * once the rebrand has settled.
 */
export const TITLE_FKA_SUFFIX_ENABLED = true;

export const BRAND_NAME_EXPONENT = 'Exponent';
export const BRAND_NAME_ACED = 'Aced';

/**
 * The brand name as a plain string, for module-level data, template
 * literals and titles. Pass {fka: true} where the rename should be made
 * explicit: "Aced (formerly Exponent)".
 */
export const getBrandName = ({fka = false} = {}) => (
  fka ? `${BRAND_NAME_ACED} (formerly ${BRAND_NAME_EXPONENT})` : BRAND_NAME_ACED
);

/**
 * The brand name as copy, for prose that mentions the product by name.
 * Pass `fka` where the rename should be made explicit, e.g. legal or
 * marketing copy: "Aced (formerly Exponent)".
 */
export const BrandName = ({fka = false}) => getBrandName({fka});

/**
 * The logo mark on its own, at a fixed square size. The badge carries its own
 * rounded-square container in the SVG, so it needs no extra clipping here.
 *
 * @param {string} [props.className] Sizing classes. The mark is square, so
 *   pass matching height and width.
 */
export const BrandMark = ({className = 'h-7 w-7'}) => (
  <img src={AcedBadge.src} alt="" aria-hidden="true" className={cx('shrink-0', className)} />
);

/**
 * The Aced wordmark as vector outlines (Plus Jakarta Sans 800), so the
 * logotype ships without a webfont. Fills with currentColor; set the size
 * with a height class and the width follows.
 */
export const AcedWordmark = ({className}) => (
  <svg
    viewBox="0 0 295 91.04"
    fill="currentColor"
    aria-hidden="true"
    className={cx('overflow-visible', className)}
  >
    <g transform="translate(-184.3 -32.48)">
      <path d="M184.30228187919462 122.1 214.13637583892617 33.89999999999999H238.52456375838926L268.3586577181208 122.1H248.35087248322148L242.43140939597316 104.1048322147651H210.1111409395973L204.191677852349 122.1ZM215.20187919463086 88.12228187919462H237.3406711409396L223.84429530201342 46.449261744966435H228.816644295302Z" />
      <path d="M304.3489932885906 123.5206711409396Q294.6410738255033 123.5206711409396 286.88657718120805 119.08107382550335Q279.1320805369127 114.64147651006711 274.5740939597315 106.94617449664429Q270.01610738255033 99.25087248322147 270.01610738255033 89.66134228187919Q270.01610738255033 80.07181208053692 274.5148993288591 72.43570469798658Q279.0136912751678 64.79959731543624 286.8273825503355 60.41919463087248Q294.6410738255033 56.03879194630872 304.3489932885906 56.03879194630872Q311.5707382550336 56.03879194630872 317.7269798657718 58.524966442953016Q323.8832214765101 61.01114093959731 328.26362416107384 65.45073825503356Q332.64402684563754 69.8903355704698 334.53825503355705 75.92818791946308L319.1476510067114 82.5579865771812Q317.49020134228186 77.70402684563757 313.5241610738255 74.86268456375838Q309.55812080536913 72.02134228187919 304.3489932885906 72.02134228187919Q299.7318120805369 72.02134228187919 296.12093959731544 74.27073825503355Q292.51006711409394 76.52013422818791 290.438255033557 80.54536912751678Q288.3664429530201 84.57060402684563 288.3664429530201 89.77973154362415Q288.3664429530201 94.98885906040267 290.438255033557 99.01409395973153Q292.51006711409394 103.03932885906039 296.12093959731544 105.28872483221475Q299.7318120805369 107.53812080536912 304.3489932885906 107.53812080536912Q309.67651006711407 107.53812080536912 313.58335570469796 104.69677852348993Q317.49020134228186 101.85543624161073 319.1476510067114 97.0014765100671L334.53825503355705 103.7496644295302Q332.76241610738253 109.43234899328858 328.3820134228188 113.93114093959731Q324.001610738255 118.42993288590604 317.84536912751673 120.97530201342282Q311.6891275167785 123.5206711409396 304.3489932885906 123.5206711409396Z" />
      <path d="M372.5412080536913 123.5206711409396Q362.2413422818792 123.5206711409396 354.66442953020135 118.96268456375839Q347.08751677852354 114.40469798657718 342.9438926174497 106.70939597315436Q338.8002684563759 99.01409395973154 338.8002684563759 89.66134228187919Q338.8002684563759 79.95342281879195 343.12147651006717 72.3765100671141Q347.4426845637584 64.79959731543624 354.78281879194634 60.41919463087248Q362.1229530201343 56.03879194630872 371.35731543624166 56.03879194630872Q379.05261744966447 56.03879194630872 384.9720805369128 58.46577181208053Q390.8915436241611 60.892751677852345 394.97597315436246 65.27315436241611Q399.06040268456377 69.65355704697986 401.1914093959732 75.39543624161072Q403.3224161073826 81.13731543624161 403.3224161073826 87.88550335570469Q403.3224161073826 89.77973154362415 403.14483221476513 91.61476510067114Q402.96724832214767 93.44979865771812 402.4936912751678 94.75208053691274H354.07248322147655V81.72926174496644H392.4306040268457L384.02496644295303 87.88550335570469Q385.2088590604027 82.79476510067113 383.90657718120804 78.82872483221476Q382.60429530201344 74.86268456375839 379.34859060402687 72.55409395973155Q376.0928859060403 70.2455033557047 371.35731543624166 70.2455033557047Q366.74013422818797 70.2455033557047 363.42523489932887 72.49489932885905Q360.11033557046983 74.74429530201341 358.4528859060403 79.12469798657716Q356.7954362416108 83.50510067114092 357.15060402684566 89.77973154362415Q356.6770469798658 95.2256375838926 358.45288590604025 99.36926174496644Q360.22872483221477 103.51288590604027 363.89879194630873 105.8214765100671Q367.5688590604027 108.13006711409395 372.77798657718125 108.13006711409395Q377.5135570469799 108.13006711409395 380.8876510067114 106.2358389261745Q384.26174496644296 104.34161073825503 386.15597315436247 101.02671140939597L400.3626845637584 107.77489932885905Q398.46845637583897 112.51046979865771 394.3840268456376 116.06214765100671Q390.29959731543624 119.6138255033557 384.7353020134228 121.56724832214765Q379.1710067114094 123.5206711409396 372.5412080536913 123.5206711409396Z" />
      <path d="M440.615033557047 123.5206711409396Q431.1438926174497 123.5206711409396 423.68536912751676 119.02187919463087Q416.2268456375839 114.52308724832214 411.9056375838926 106.82778523489932Q407.58442953020136 99.1324832214765 407.58442953020136 89.77973154362415Q407.58442953020136 80.30859060402685 411.9648322147651 72.67248322147651Q416.3452348993289 65.03637583892618 423.80375838926176 60.53758389261745Q431.26228187919463 56.03879194630872 440.496644295302 56.03879194630872Q447.6 56.03879194630872 453.105100671141 58.702550335570464Q458.6102013422819 61.36630872483221 461.806711409396 66.22026845637583L459.0837583892618 69.77194630872484V32.4793288590604H476.8421476510067V122.1H460.26765100671145V110.26107382550335L461.92510067114097 113.93114093959731Q458.6102013422819 118.66671140939597 452.9275167785235 121.09369127516779Q447.2448322147651 123.5206711409396 440.615033557047 123.5206711409396ZM442.7460402684564 107.53812080536912Q447.6 107.53812080536912 451.270067114094 105.28872483221475Q454.94013422818796 103.03932885906039 457.0119463087249 99.01409395973153Q459.0837583892618 94.98885906040267 459.0837583892618 89.77973154362415Q459.0837583892618 84.57060402684563 457.0119463087249 80.54536912751678Q454.94013422818796 76.52013422818791 451.270067114094 74.27073825503355Q447.6 72.02134228187919 442.7460402684564 72.02134228187919Q437.8920805369128 72.02134228187919 434.04442953020134 74.32993288590603Q430.19677852348997 76.63852348993288 428.0657718120806 80.60456375838925Q425.93476510067114 84.57060402684563 425.93476510067114 89.77973154362415Q425.93476510067114 94.98885906040267 428.0657718120806 99.01409395973153Q430.19677852348997 103.03932885906039 434.04442953020134 105.28872483221475Q437.8920805369128 107.53812080536912 442.7460402684564 107.53812080536912Z" />
    </g>
  </svg>
);

/**
 * Mark plus wordmark. The wordmark is an inline vector so it inherits colour
 * via currentColor and scales crisply without shipping a webfont.
 *
 * @param {boolean} [props.wordmarkHidden] Fade the wordmark out without
 *   unmounting it, so a collapsing sidebar has something to animate.
 * @param {boolean} [props.fkaBadge] Show a small "previously Exponent"
 *   badge after the Aced wordmark (subject to FKA_BADGE_ENABLED). For the
 *   headers; leave off in cramped or repeated placements.
 * @param {string} [props.markClassName] Sizing for the mark.
 * @param {string} [props.wordmarkClassName] Extra classes for the wordmark.
 * @param {string} [props.slotClassName] Wraps the mark in a fixed-size box.
 *   The sidebar passes its shared icon slot so the mark lines up with the nav
 *   icons below it and holds position while the panel resizes.
 */
export const BrandLockup = ({
  wordmarkHidden = false,
  fkaBadge = false,
  markClassName,
  wordmarkClassName,
  slotClassName,
}) => {
  const mark = <BrandMark className={markClassName} />;

  return (
    <>
      {slotClassName ? <span className={slotClassName}>{mark}</span> : mark}
      <span
        className={cx(
          'flex items-center gap-2 whitespace-nowrap',
          wordmarkClassName,
          wordmarkHidden && 'opacity-0',
        )}
      >
        <AcedWordmark className="h-3.5 w-auto shrink-0 text-gray-900" />
        {fkaBadge && FKA_BADGE_ENABLED && (
          <span className="mt-1 text-xs font-medium leading-none text-gray-500">
            {`formerly ${BRAND_NAME_EXPONENT}`}
          </span>
        )}
      </span>
    </>
  );
};

/**
 * The dimmed wordmark shown in the corner of video call screens (coaching
 * sessions and practice/duet sessions alike).
 */
export const VideoWatermark = () => (
  <AcedWordmark className="absolute top-7 left-8 opacity-50 w-14 text-white" />
);

/**
 * The clickable brand lockup used in the sidebar, top bar and legacy header.
 */
const Brand = ({
  href,
  className,
  onClick,
  ...lockupProps
}) => {
  const name = getBrandName();
  const {currentUser} = useAppContext();

  // Signed-in users' dashboard is /home; a client-side nav to / bypasses the
  // server redirect.
  const resolvedHref = href || (currentUser ? '/home?src=nav' : '/?src=nav');

  return (
    <Link
      href={resolvedHref}
      onClick={onClick}
      aria-label={`${name} home`}
      className={cx('flex min-w-0 items-center', className)}
    >
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <BrandLockup {...lockupProps} />
    </Link>
  );
};

export default Brand;
